import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from "@angular/core";
import { ShortSearchCalendarMonth } from "../../short-search-params.facade";

@Component({
  selector: "app-short-search-dates-dropdown",
  templateUrl: "./short-search-dates-dropdown.component.html",
  styleUrls: ["./short-search-dates-dropdown.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class ShortSearchDatesDropdownComponent {
  @Input() months: ShortSearchCalendarMonth[] = [];
  @Input() weekDays: string[] = [];
  @Input() selectedDepartureDate: string;
  @Input() isFlexibleDates = false;
  @Input() showFlexibleDates = true;
  @Input() hasLoadedAvailableDates = false;
  @Input() availableDateKeys = new Set<string>();
  @Input() minDateKey: string | null = null;
  @Input() maxDateKey: string | null = null;

  @Output() prevMonth = new EventEmitter<void>();
  @Output() nextMonth = new EventEmitter<void>();
  @Output() dateSelected = new EventEmitter<string>();
  @Output() flexibleDatesChange = new EventEmitter<boolean>();

  onFlexibleDatesChange(event: Event) {
    event.stopPropagation();
    this.flexibleDatesChange.emit((event.target as HTMLInputElement).checked);
  }

  isDateAvailable(key: string | null) {
    if (!key || !this.hasLoadedAvailableDates) {
      return false;
    }

    return this.availableDateKeys.has(key);
  }

  canSelectDate(key: string | null) {
    if (!key) {
      return false;
    }

    const date = this.parseDateKey(key);
    if (!date) {
      return false;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);

    if (date.getTime() < today.getTime()) {
      return false;
    }

    const minDate = this.parseDateKey(this.minDateKey);
    if (minDate) {
      minDate.setHours(0, 0, 0, 0);
      if (date.getTime() < minDate.getTime()) {
        return false;
      }
    }

    const maxDate = this.parseDateKey(this.maxDateKey);
    if (maxDate) {
      maxDate.setHours(0, 0, 0, 0);
      if (date.getTime() > maxDate.getTime()) {
        return false;
      }
    }

    return true;
  }

  isDateSelected(key: string | null) {
    return !!key && this.selectedDepartureDate === key;
  }

  isDateInRange(key: string | null) {
    if (!key || !this.isFlexibleDates || !this.selectedDepartureDate) {
      return false;
    }

    const selected = this.parseDateKey(this.selectedDepartureDate);
    const current = this.parseDateKey(key);
    if (!selected || !current) {
      return false;
    }

    const rangeStart = this.addDays(selected, -2).getTime();
    const rangeEnd = this.addDays(selected, 2).getTime();
    const currentTime = current.getTime();

    return currentTime >= rangeStart && currentTime <= rangeEnd;
  }

  isRangeStart(key: string | null) {
    if (!key || !this.isFlexibleDates || !this.selectedDepartureDate) {
      return false;
    }

    const selected = this.parseDateKey(this.selectedDepartureDate);
    if (!selected) {
      return false;
    }

    return this.formatDateKey(this.addDays(selected, -2)) === key;
  }

  isRangeEnd(key: string | null) {
    if (!key || !this.isFlexibleDates || !this.selectedDepartureDate) {
      return false;
    }

    const selected = this.parseDateKey(this.selectedDepartureDate);
    if (!selected) {
      return false;
    }

    return this.formatDateKey(this.addDays(selected, 2)) === key;
  }

  private parseDateKey(value: string) {
    if (!value) {
      return null;
    }

    const [year, month, day] = value.split("-").map(Number);
    if (!year || !month || !day) {
      return null;
    }

    return new Date(year, month - 1, day);
  }

  private formatDateKey(date: Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  private addDays(date: Date, days: number) {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
  }
}
